import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Eye, EyeOff, LogIn, AlertCircle } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate("/dashboard");
    } else if (result.error && result.error.includes("verify your email")) {
      setNeedsVerification(true);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col items-center justify-start p-8 pt-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_build-flow-6/artifacts/1y1lfwdp_Omnas%20Logo%20Combined%20%281%29.png"
              alt="OMNΛS"
              className="h-96 w-auto"
            />
          </div>

          <div className="space-y-1 mb-4 -mt-20">
            <h1 className="text-3xl font-bold font-heading tracking-tight">
              Welcome back
            </h1>
            <p className="text-muted-foreground">
              Sign in to your account to continue
            </p>
          </div>

          {needsVerification && (
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-200">
                Please verify your email before logging in.{" "}
                <Link to="/resend-verification" className="text-primary hover:underline font-medium">
                  Resend verification email
                </Link>
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="bg-muted/50 border-border focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="bg-muted/50 border-border focus:ring-primary pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground glow-primary-strong"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn size={18} />
                  Sign in
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-muted-foreground">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-primary hover:underline font-medium"
              data-testid="register-link"
            >
              Create one
            </Link>
          </p>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Subcontractor or crew member? <Link to="/portal/login" className="text-primary hover:underline font-medium">Use the Worker Portal</Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1920&q=80')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 p-6 glass-surface rounded-xl">
          <h2 className="text-xl font-semibold font-heading mb-2">
            AI-Powered Construction Management
          </h2>
          <p className="text-muted-foreground text-sm">
            Streamline your projects with intelligent scheduling, procurement tracking, and resource optimization.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
